import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi'
import {FAQItem} from 'components/FAQs'
import {addDays} from 'date-fns/addDays'
import {format} from 'date-fns/format'
import {parseISO} from 'date-fns/parseISO'
import {useApSeoBreadcrumb} from 'hooks/useApSeoBreadcrumb'
import {useHotelSlug} from 'hooks/useHotelSlug'
import {usePricePrediction} from 'hooks/usePricePrediction'
import {
  SeoAPMetadata,
  useSeoAPMetadata
} from 'hooks/useSeoAPMetadata/useSeoAPMetadata'
import {getMetaBrandHasRooms} from 'modules/accommodation/selectors'
import {getCurrencyCode, getLocaleCode} from 'modules/meta/selectors'
import {
  getFAQData,
  getHotelResult,
  getHotelRoomsData
} from 'modules/searchApi/selectors'
import {getHotelDescriptionData} from 'modules/seoApi/selectors'
import Head from 'next/head'
import {getRankedFacilitiesFromContentHotel} from 'utils/facilities'
import {imageProvider} from 'utils/imageProvider'
import {
  getBreadcrumbSchema,
  getOrganizationSchema,
  getSchemaFromHotel,
  getWebsiteSchema,
  SEO_IMAGE_SIZE
} from 'utils/seo/getJsonLdSchemas'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {htmlToPlainText} from '@vio/core/src/_web/utils/dom'
import {formatPriceValue} from '@vio/core/src/price/business/formatPrice'
import {
  IMPERIAL_UNIT,
  METRIC_UNIT
} from '@vio/core/src/room/business/utils/roomAreaUnit'
import {Room, RoomArea} from '@vio/core/src/room/types/room'
import {UTS_DATE_FORMAT} from '@vio/core/src/utils/date'
import {
  applyRoundingStrategy,
  getRoundingStrategy
} from '@vio/core/src/utils/number'
import {getTopRoomAmenities} from '@vio/shared/src/accommodation/RoomAmenities/utils'
import {
  getVisiblePricePredictionEntries,
  PricePredictionEntry
} from '@vio/shared/src/search/DatePicker/PricePrediction/helpers'

import type {HotelDescription} from 'seo/seo-db/queries/hotelDescription'

const MAX_ROOM_AMENITIES = 3
const MAX_SEO_IMAGES = 3

export interface PriceCalendarSchema {
  priceRange: string
  makesOffer: Record<string, unknown>
}

/**
 * Represents the price prediction calendar as an `AggregateOffer` with one
 * `Offer` per visible night, plus the free-text `priceRange` for the `Hotel`
 * node. Returns null when no priced night is visible, so the caller can fall
 * back to the flat anchor-price offer.
 *
 * Prices are rounded the same way as the calendar cells, so the structured
 * data always matches what the user sees.
 */
export const getPriceCalendarSchema = ({
  prices,
  hotelPageUrl,
  currencyCode,
  locale,
  nights,
  occupancy,
  referenceDate = new Date()
}: {
  prices: PricePredictionEntry[]
  hotelPageUrl: string
  currencyCode: string
  locale: string
  nights: number
  occupancy: number
  referenceDate?: Date
}): PriceCalendarSchema | null => {
  const visibleEntries = getVisiblePricePredictionEntries(prices, referenceDate)
  if (visibleEntries.length === 0) return null

  // Prices refresh multiple times a day, so they are valid until tomorrow
  const priceValidUntil = format(addDays(referenceDate, 1), UTS_DATE_FORMAT)
  const availability = 'https://schema.org/InStock'

  const offers = visibleEntries.map(({checkIn, price}) => {
    const checkOut = format(addDays(parseISO(checkIn), nights), UTS_DATE_FORMAT)

    return {
      '@type': 'Offer',
      priceCurrency: currencyCode,
      price: applyRoundingStrategy(getRoundingStrategy(), price),
      availability,
      availabilityStarts: checkIn,
      availabilityEnds: checkOut,
      priceValidUntil,
      // Deliberately without checkIn/checkOut params, to keep non-canonical
      // URLs out of search indexes; the dates live in availabilityStarts/Ends.
      url: hotelPageUrl
    }
  })

  const priceValues = offers.map(({price}) => price)
  const lowPrice = Math.min(...priceValues)
  const highPrice = Math.max(...priceValues)
  const formatNightlyPrice = (value: number) =>
    formatPriceValue({
      locale,
      value,
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })

  return {
    priceRange: `${formatNightlyPrice(lowPrice)}–${formatNightlyPrice(highPrice)} per night`,
    makesOffer: {
      '@type': 'AggregateOffer',
      '@id': `${hotelPageUrl}#price-calendar`,
      priceCurrency: currencyCode,
      lowPrice,
      highPrice,
      offerCount: offers.length,
      availability,
      availabilityStarts: visibleEntries[0].checkIn,
      availabilityEnds: visibleEntries[visibleEntries.length - 1].checkIn,
      priceValidUntil,
      url: hotelPageUrl,
      eligibleQuantity: {
        '@type': 'QuantitativeValue',
        value: occupancy,
        unitText: 'guests'
      },
      offers
    }
  }
}

const getFloorSizeSchema = (area: RoomArea | undefined) => {
  if (area?.squareMeters) {
    return {
      '@type': 'QuantitativeValue',
      value: Math.round(area.squareMeters),
      unitCode: 'MTK',
      unitText: METRIC_UNIT
    }
  }

  if (area?.squareFeet) {
    return {
      '@type': 'QuantitativeValue',
      value: Math.round(area.squareFeet),
      unitCode: 'FTK',
      unitText: IMPERIAL_UNIT
    }
  }

  return null
}

const getBedSchema = (room: Room) => {
  const beds = (room.bedrooms ?? [])
    .flatMap(({bedConfigurations = []}) => bedConfigurations)
    .filter(({type}) => Boolean(type))
    .map(({count, type}) => ({
      '@type': 'BedDetails',
      numberOfBeds: count,
      typeOfBed: type
    }))

  if (beds.length === 0) return null

  return beds.length === 1 ? beds[0] : beds
}

export const getHotelRoomSchemas = ({
  rooms,
  hotelPageUrl
}: {
  rooms: Room[]
  hotelPageUrl: string
}) =>
  rooms
    .filter(({name}) => Boolean(name))
    .map(room => {
      const roomUrl = `${hotelPageUrl}#room-${room.id}`
      const description = room.description
        ? htmlToPlainText(room.description)
        : ''
      const imageUrl = room.images?.[0]?.url
      const bed = getBedSchema(room)
      const floorSize = getFloorSizeSchema(room.area)
      const maxOccupancy =
        room.occupancy?.maxOccupancy ?? room.occupationPerRoom
      // The same ranking the room card shows, so the markup describes what is
      // on the page. A room can list dozens of amenities; the tail is noise.
      const amenityFeature = getTopRoomAmenities(room.amenitiesList)
        .slice(0, MAX_ROOM_AMENITIES)
        .map(({title}) => ({
          '@type': 'LocationFeatureSpecification',
          name: title,
          value: true
        }))

      return {
        '@type': 'HotelRoom',
        '@id': roomUrl,
        name: room.name,
        url: roomUrl,
        containedInPlace: {'@id': `${hotelPageUrl}#hotel`},
        ...(description && {description}),
        ...(imageUrl && {image: [imageProvider(imageUrl, SEO_IMAGE_SIZE)]}),
        ...(bed && {bed}),
        ...(maxOccupancy && {
          occupancy: {
            '@type': 'QuantitativeValue',
            maxValue: maxOccupancy,
            unitText: 'person'
          }
        }),
        ...(floorSize && {floorSize}),
        ...(amenityFeature.length && {amenityFeature})
      }
    })

export const getHotelSchema = ({
  hotel,
  hotelPageUrl,
  anchorPrice,
  currencyCode,
  priceCalendar,
  roomIds = [],
  seoDescription
}: {
  hotel: ContentHotel
  hotelPageUrl: string
  anchorPrice: number | undefined
  currencyCode: string
  priceCalendar?: PriceCalendarSchema | null
  roomIds?: string[]
  seoDescription?: HotelDescription
}) => {
  if (!hotel) return null

  const facilities = getRankedFacilitiesFromContentHotel(hotel)

  const amenityFeature = facilities.map(facility => ({
    '@type': 'LocationFeatureSpecification',
    name: facility.value,
    value: true
  }))

  const makesOffer =
    priceCalendar?.makesOffer ??
    (anchorPrice
      ? {
          '@type': 'Offer',
          url: hotelPageUrl,
          price: anchorPrice,
          priceCurrency: currencyCode
        }
      : undefined)

  return {
    '@type': 'Hotel',
    '@id': `${hotelPageUrl}#hotel`,
    name: hotel.hotelName,
    url: hotelPageUrl,
    ...getSchemaFromHotel(
      'Description',
      {descriptions: {...hotel.descriptions, ...seoDescription}},
      {preferHeadline: Boolean(seoDescription)}
    ),
    ...getSchemaFromHotel('Image', hotel, {
      maxNumberOfImages: MAX_SEO_IMAGES
    }),
    ...(hotel._geoloc && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: hotel._geoloc.lat,
        longitude: hotel._geoloc.lon
      }
    }),
    ...(hotel.checkInTime && {checkinTime: hotel.checkInTime}),
    ...(hotel.checkOutTime && {checkoutTime: hotel.checkOutTime}),
    ...(amenityFeature.length && {amenityFeature}),
    ...(priceCalendar && {priceRange: priceCalendar.priceRange}),
    ...(makesOffer && {makesOffer}),
    ...getSchemaFromHotel('Rating', hotel),
    ...getSchemaFromHotel('AggregateRating', hotel),
    ...getSchemaFromHotel('PostalAddress', hotel),
    ...(roomIds.length && {
      containsPlace: roomIds.map(roomId => ({'@id': roomId}))
    })
  }
}

/**
 * The visible "FAQs about the hotel" section as an `FAQPage`.
 */
export const getFaqPageSchema = ({
  faqs,
  hotelPageUrl
}: {
  faqs: FAQItem[]
  hotelPageUrl: string
}) => {
  const mainEntity = faqs
    .filter(({question, answer}) => Boolean(question) && Boolean(answer))
    .map(({question, answer}) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer
      }
    }))

  if (mainEntity.length === 0) return null

  return {
    '@type': 'FAQPage',
    '@id': `${hotelPageUrl}#faq`,
    mainEntity
  }
}

export const getHotelPageSchema = ({
  baseUrl,
  hotelPageUrl,
  metadata,
  locale
}: {
  baseUrl: string
  hotelPageUrl: string
  metadata: SeoAPMetadata | null
  locale: string
}) => {
  if (!metadata) return null

  return {
    '@type': 'WebPage',
    '@id': `${hotelPageUrl}`,
    url: `${hotelPageUrl}`,
    name: metadata.title,
    description: metadata.description,
    inLanguage: locale,
    breadcrumb: {
      '@id': `${hotelPageUrl}#breadcrumb`
    },
    mainEntity: {
      '@id': `${hotelPageUrl}#hotel`
    },
    sourceOrganization: {
      '@id': `${baseUrl}/#organization`
    }
  }
}

export const SeoAPStructuredData: React.FC = () => {
  const {brand} = useBrand()
  const locale = useSelector(getLocaleCode)
  const currencyCode = useSelector(getCurrencyCode)
  const hotelSlug = useHotelSlug()
  const {data: hotel} = useSelector(state => getHotelResult(state, hotelSlug))
  const metadata = useSeoAPMetadata()
  const breadcrumb = useApSeoBreadcrumb()
  const brandHasRooms = useSelector(getMetaBrandHasRooms)
  const rooms = useSelector(state =>
    getHotelRoomsData(state, hotel?.objectID ?? '')
  )

  const faqsData = useSelector(state =>
    getFAQData(state, hotel?.objectID ?? '')
  )

  const seoDescription = useSelector(state =>
    getHotelDescriptionData(state, hotel?.objectID ?? '')
  )

  const {prices, nights, occupancy} = usePricePrediction(hotel?.objectID)

  const schemas = useMemo(() => {
    if (!metadata || !hotel) return null

    const baseUrl = brand.siteAddresses.address
    const hotelPageUrl = metadata.canonical
    const anchorPrice = hotel.pricing?.minRate

    const priceCalendar = prices?.length
      ? getPriceCalendarSchema({
          prices,
          hotelPageUrl,
          currencyCode,
          locale,
          nights,
          occupancy
        })
      : null

    // Mirrors the room listing: no listing rendered, no room nodes emitted
    const roomSchemas = brandHasRooms
      ? getHotelRoomSchemas({rooms, hotelPageUrl})
      : []

    return [
      getOrganizationSchema({brand}),
      getWebsiteSchema({brand}),
      getBreadcrumbSchema({baseUrl, breadcrumb, seoPageUrl: hotelPageUrl}),
      getHotelSchema({
        hotel,
        hotelPageUrl,
        anchorPrice,
        currencyCode,
        priceCalendar,
        roomIds: roomSchemas.map(({'@id': roomId}) => roomId),
        seoDescription
      }),
      ...roomSchemas,
      getFaqPageSchema({faqs: faqsData?.faqs ?? [], hotelPageUrl}),
      getHotelPageSchema({baseUrl, hotelPageUrl, metadata, locale})
    ].filter(Boolean)
  }, [
    brand,
    locale,
    currencyCode,
    hotel,
    metadata,
    breadcrumb,
    prices,
    nights,
    occupancy,
    rooms,
    brandHasRooms,
    faqsData,
    seoDescription
  ])

  if (!schemas) return null

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': schemas
          })
        }}
      />
    </Head>
  )
}
